import { cn } from "src/lib/utils";

export type StatusValue = "paid" | "pending" | "partial" | "overdue" | "active" | "instock" | "low" | "out" | "approved" | "declined";

const statusStyles: Record<StatusValue, string> = {
	paid: "tw-bg-teal-tint tw-text-teal-dark",
	approved: "tw-bg-teal-tint tw-text-teal-dark",
	instock: "tw-bg-teal-tint tw-text-teal-dark",
	pending: "tw-bg-amber-tint tw-text-amber",
	low: "tw-bg-amber-tint tw-text-amber",
	partial: "tw-bg-blue-tint tw-text-blue",
	active: "tw-bg-blue-tint tw-text-blue",
	overdue: "tw-bg-red-tint tw-text-red",
	declined: "tw-bg-red-tint tw-text-red",
	out: "tw-bg-red-tint tw-text-red",
};

const statusLabels: Partial<Record<StatusValue, string>> = {
	instock: "In Stock",
	out: "Out of Stock",
	low: "Low Stock",
};

interface StatusBadgeProps {
	/**
	 * A known status, or any other string the API sends — unknown values fall back to the
	 * neutral slate style rather than failing to render.
	 */
	status: StatusValue | (string & {});
	label?: string;
	className?: string;
}

/**
 * Prototype `.status` badge.
 *
 * The prototype conveys status by colour alone (spec §6); this adds a filled dot whose
 * shape is consistent across states plus always-present text, so the badge is still
 * readable without colour.
 */
const StatusBadge = ({ status, label, className }: StatusBadgeProps) => (
	<span
		className={cn(
			"tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[11.5px] tw-font-bold tw-capitalize",
			statusStyles[status as StatusValue] ?? "tw-bg-slate-tint tw-text-slate",
			className
		)}
	>
		<span className="tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-current" aria-hidden="true" />
		{label ?? statusLabels[status as StatusValue] ?? status}
	</span>
);

export default StatusBadge;
